export const ACCOUNTING_METHODS = ['wire', 'ach', 'card'];
export const ACCOUNTING_READINESS = {
  not_configured: 'Accounting mapping is not configured.',
  disabled: 'Accounting posting is disabled.',
  needs_verification: 'Accounting mapping needs verification.',
  sandbox_only: 'Accounting is limited to sandbox testing.',
  ready: 'Sandbox accounting checks are ready. Live activation remains off.',
};
const object = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const exact = (value, keys) =>
  object(value) &&
  Object.keys(value).length === keys.length &&
  Object.keys(value).every((key) => keys.includes(key));
const noControlCharacters = (value) =>
  [...value].every((character) => character.charCodeAt(0) > 31 && character.charCodeAt(0) !== 127);
const identifier = (value) =>
  typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/.test(value);
export const emptyAccountingConfiguration = () => ({
  legalBuyerName: '',
  qbRealmId: '',
  qbCustomerId: '',
  environment: '',
  familyItems: {},
  depositAccounts: {},
  tax: { approved: false, code: '' },
  postingDatePolicy: '',
});
export function validAccountingConfiguration(value, families, allowUnavailable = false) {
  return Boolean(
    exact(value, [
      'legalBuyerName',
      'qbRealmId',
      'qbCustomerId',
      'environment',
      'familyItems',
      'depositAccounts',
      'tax',
      'postingDatePolicy',
    ]) &&
      typeof value.legalBuyerName === 'string' &&
      value.legalBuyerName === value.legalBuyerName.trim() &&
      value.legalBuyerName.length > 0 &&
      value.legalBuyerName.length <= 200 &&
      noControlCharacters(value.legalBuyerName) &&
      typeof value.qbRealmId === 'string' &&
      /^\d{1,80}$/.test(value.qbRealmId) &&
      identifier(value.qbCustomerId) &&
      ['sandbox', 'production'].includes(value.environment) &&
      object(value.familyItems) &&
      Object.keys(value.familyItems).length > 0 &&
      Object.entries(value.familyItems).every(
        ([key, mapping]) =>
          families.some(
            (family) => family.key === key && (allowUnavailable || family.available === true)
          ) &&
          exact(mapping, ['qbItemId', 'incomeAccountId']) &&
          identifier(mapping.qbItemId) &&
          identifier(mapping.incomeAccountId)
      ) &&
      object(value.depositAccounts) &&
      Object.keys(value.depositAccounts).length > 0 &&
      Object.entries(value.depositAccounts).every(
        ([method, account]) => ACCOUNTING_METHODS.includes(method) && identifier(account)
      ) &&
      exact(value.tax, ['approved', 'code']) &&
      value.tax.approved === true &&
      value.tax.code === 'NON' &&
      value.postingDatePolicy === 'verified_clearance_date'
  );
}
export function readAccountingSettings(response, distributorId) {
  const value = response?.data?.data;
  if (
    value?.distributorId !== distributorId ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    value.revision >= Number.MAX_SAFE_INTEGER ||
    value.currency !== 'USD' ||
    value.treatment !== 'sale_on_clearance' ||
    !Array.isArray(value.families) ||
    value.families.length > 100 ||
    value.families.some(
      (family) =>
        typeof family?.key !== 'string' ||
        !/^[a-z][a-z0-9_]{1,39}$/.test(family.key) ||
        typeof family.available !== 'boolean' ||
        typeof family.label !== 'string' ||
        !family.label.trim() ||
        family.label.length > 160
    ) ||
    new Set(value.families.map((family) => family.key)).size !== value.families.length ||
    (value.configuration !== null &&
      !validAccountingConfiguration(value.configuration, value.families, true)) ||
    typeof value.readiness?.ready !== 'boolean' ||
    !Object.hasOwn(ACCOUNTING_READINESS, value.readiness.code) ||
    (value.readiness.ready &&
      (value.readiness.code !== 'ready' || value.configuration?.environment !== 'sandbox')) ||
    (!value.readiness.ready && value.readiness.code === 'ready')
  )
    throw new Error('Invalid accounting settings');
  return value;
}
const canonical = (value) => {
  if (!object(value)) return JSON.stringify(value);
  return JSON.stringify(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonical(value[key])])
  );
};
export const sameAccountingConfiguration = (a, b) => canonical(a) === canonical(b);
